import React from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '../../animations/animations';
import { MdSecurity, MdBuild, MdBugReport, MdDeveloperBoard } from 'react-icons/md';

const competencies = [
  {
    icon: MdSecurity,
    title: 'Secure Firmware Development',
    description: 'Built production OTA systems with streaming AES-256-CBC decryption, RSA-2048 signature verification, SHA-256 downgrade protection, and structured exit-code protocols - all before any file touches the target filesystem.',
    color: 'from-green-500 to-emerald-600',
  },
  {
    icon: MdBuild,
    title: 'Build Systems & Toolchains',
    description: 'Built custom ARM cross-compiler toolchains from scratch using crosstool-ng, maintained Buildroot-based rootfs configs, and packaged full dev environments as .deb files - cutting new engineer onboarding from half a day to minutes.',
    color: 'from-orange-500 to-amber-500',
  },
  {
    icon: MdBugReport,
    title: 'Debugging & Reliability',
    description: 'Diagnosed persistent heap leaks via custom memory sampling, traced OOM events through dmesg and kernel panic logs, and found IPC race conditions with GDB and Valgrind. Brought fleet uptime from sub-95% to 99.9%.',
    color: 'from-red-500 to-rose-500',
  },
  {
    icon: MdDeveloperBoard,
    title: 'Reverse Engineering & BSP',
    description: 'Reverse engineered a proprietary OEM firmware stack with no vendor docs using a logic analyzer, Wireshark, and Ghidra. Worked around a locked bootloader by running a full firmware stack as a namespaced process via `unshare`.',
    color: 'from-purple-500 to-violet-600',
  },
];

const CompetencyCard = ({ icon: Icon, title, description, color, index }) => {
  return (
    <motion.div
      variants={staggerItem}
      className="card group cursor-default"
      data-aos="fade-up"
      data-aos-delay={index * 100}
    >
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-textSecondary leading-relaxed text-sm">{description}</p>
    </motion.div>
  );
};

const Competencies = () => {
  return (
    <section className="py-24 px-6 bg-darkSurface/30">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ y: 60, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
          className="section-header"
        >
          Core Competencies
        </motion.h2>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {competencies.map((comp, index) => (
            <CompetencyCard key={index} {...comp} index={index} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Competencies;
